<template>
    <aside class="settings-content settings-script">
        <Scrollbar class="data-list" :show_bar="true">
            <div class="head">
                <div class="title">Script Manager</div>
            </div>

            <div class="desc">Utilize the integrations below to add and update your own tags for conversion tracking, site analytics, re-marketing, and more.</div>
            <Alert v-if="!is_merchant_host" svgName="lock" className="yellow">
                <p>
                    Your current plan does not have access to this feature. 
                    <a :href="`mailto:anchor@motom.me?subject=${$store.state.user_data.merchant_account.merchant_display_name} - Upgrade to Custom Subdomain&body= Please provide your name and contact info. A representative will contact you in 1-2 business days.`" target="_blank">Contact us</a> 
                    to upgrade your account
                </p>
            </Alert>
            <Loading class="static" v-if="is_loading" />

            <div v-else :class="[!permission_script &&'not-allowed-box']">
                <div class="script-box" :class="{'is-loading': gtm.is_loading}">
                    <div class="box" @click="gtmHandle()" v-if="!gtm.is_edit">
                        <img :src="require('@/assets/images/google-tag.png')">
                        <div class="text-id">
                            <h3>Google Tag Manager</h3>
                            <p v-if="gtm.id">{{ gtm.container_id }}</p>
                            <p v-else>Example: {{ ex_gtm }}</p>
                        </div>
                        <div class="right">
                            <time v-if="gtm.id">{{ moment(gtm.updated_at).format('MMM DD, YYYY') }}</time>
                            <div v-if="gtm.id" :class="['act', !gtm.status && 'act-disabled']">
                                {{ (gtm.status === 100) ? 'Active' : 'Disabled' }}
                            </div>
                            <div v-if="is_merchant_host && gtm.id && permission_script" class="svg-box">
                                <svg-icon name="pen" />
                            </div>
                            <el-button v-else-if="is_merchant_host && !gtm.id" type="primary" :disabled="!permission_script">Add script</el-button>
                            <span v-else-if="permission_script" class="link">Learn more</span>
                        </div>
                    </div>
                    <div class="edit-box" v-else>
                        <header><h2>Google Tag Manager</h2> <time v-if="gtm.id">Last update: {{ moment(gtm.updated_at).format('MMM DD, YYYY') }}</time> <i @click="close('gtm')"><svg-icon name="close" /></i></header>
                        <div class="text">
                            <img :src="require('@/assets/images/google-tag.png')">
                            <p>Google Tag Manager (GTM) is a tool that allows you to quickly and easily adjust tracking and marketing automation scripts for your pages from one centralized place. For more info on Google Tag Manager, check out Google's documentation: <a href="https://support.google.com/tagmanager/answer/6102821?hl=en" target="_blank">Tag Manager Overview</a>.</p>
                        </div>
                        <div class="form-item" v-if="is_merchant_host">
                            <label>Container ID</label>
                            <input v-model.trim="gtm.container_id" :class="{'error': gtm.is_show_error}" type="text" :placeholder="'Example: ' + ex_gtm" />
                        </div>
                        <div class="form-item" v-else>
                            <label style="width: auto;">Container ID:  {{ ex_gtm }}</label>
                        </div>
                        <p class="error-msg" v-if="gtm.is_show_error">{{ err_message }}</p>
                        <div class="form-submit" v-if="is_merchant_host">
                            <div><span  v-if="gtm.id" @click.native="gtm.status === 100? gtm.status = 0 : gtm.status = 100; save('gtm');">Enable <Switch :checked="(gtm.status === 100)" /></span></div>
                            <span class="remove" @click="remove('gtm')" v-if="gtm.id">Remove</span>
                            <el-button @click="save('gtm')" type="primary" :disabled="!permission_script">{{ gtm.id ? 'Save' : 'Add' }}</el-button>
                        </div>
                    </div>
                </div>
                <div class="script-box" :class="{'is-loading': ga4.is_loading}">
                    <div class="box" @click="ga4Handle()" v-if="!ga4.is_edit">
                        <img :src="require('@/assets/images/google-analytics.png')">
                        <div class="text-id">
                            <h3>Google Analytics 4</h3>
                            <p v-if="ga4.id">{{ ga4.container_id }}</p>
                            <p v-else>Example: {{ ex_ga4 }}</p>
                        </div>
                        <div class="right">
                            <time v-if="ga4.id">{{ moment(ga4.updated_at).format('MMM DD, YYYY') }}</time>
                            <div v-if="ga4.id" :class="['act', !ga4.status && 'act-disabled']">
                                {{ (ga4.status === 100) ? 'Active' : 'Disabled' }}
                            </div>
                            <div v-if="is_merchant_host && ga4.id && permission_script" class="svg-box">
                                <svg-icon name="pen" />
                            </div>
                            <el-button v-else-if="is_merchant_host && !ga4.id" type="primary" :disabled="!permission_script">Add script</el-button>
                            <span v-else-if="permission_script" class="link">Learn more</span>
                        </div>
                    </div>
                    <div class="edit-box" v-else>
                        <header><h2>Google Analytics 4</h2> <time v-if="ga4.id">Last update: {{ moment(ga4.updated_at).format('MMM DD, YYYY') }}</time> <i @click="close('ga4')"><svg-icon name="close" /></i></header>
                        <div class="text">
                            <img :src="require('@/assets/images/google-analytics.png')">
                            <p>Google Analytics 4 is an analytics service that enables you to measure traffic and engagement across your websites and apps. This documentation provides implementation instructions and reference materials geared towards a developer audience. For more info on Google Analytics 4, check out Google's documentation: <a href="https://support.google.com/analytics/answer/10089681?hl=en" target="_blank">Google Analytics Overview</a>.</p>
                        </div>
                        <div class="form-item" v-if="is_merchant_host">
                            <label>Container ID</label>
                            <input v-model.trim="ga4.container_id" :class="{'error': ga4.is_show_error}" type="text" :placeholder="'Example: ' + ex_ga4" />
                        </div>
                        <div class="form-item" v-else>
                            <label style="width: auto;">Container ID:  {{ ex_ga4 }}</label>
                        </div>
                        <p class="error-msg" v-if="ga4.is_show_error">{{ err_message }}</p>
                        <div class="form-submit" v-if="is_merchant_host">
                            <div><span  v-if="ga4.id" @click.native="ga4.status === 100? ga4.status = 0 : ga4.status = 100; save('ga4');">Enable <Switch :checked="(ga4.status === 100)" /></span></div>
                            <span class="remove" @click="remove('ga4')" v-if="ga4.id">Remove</span>
                            <el-button @click="save('ga4')" type="primary" :disabled="!permission_script">{{ ga4.id ? 'Save' : 'Add' }}</el-button>
                        </div>
                    </div>
                </div>
            </div>
        </Scrollbar>
    </aside>
</template>

<script>
import Button from '@/components/ui/Button.vue';
import Loading from '@/components/ui/Loading.vue';
import Switch from '@/components/ui/Switch.vue';
import Alert from '@/components/settings/SettingAlert.vue';
import moment from 'moment';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import { ElButton } from 'element-plus';

export default {
    name: 'Domain',

    components: {
        Button,
        Loading,
        Switch,
        Alert,
        Scrollbar,
        ElButton
    },

    data() {
        return {
            is_loading: true,
            is_merchant_host: false,
            ex_ga4: 'G-123456789',
            ex_gtm: 'GTM-123456789',
            gtm: {
                is_edit: false,
                is_show_error: false,
                is_loading: false,
                id: 0,
                updated_at: '',
                container_id: '',
            },
            ga4: {
                is_edit: false,
                is_show_error: false,
                is_loading: false,
                id: 0,
                updated_at: '',
                container_id: '',
            },
            err_message:''
        }
    },

    computed:{
        permission_script(){
            return this.$store.state.user_data?.permission_list?.settingIntegrations?.scriptManagerSettings.permission === 100;
        },
    },

    methods: {
        moment: moment,
        gtmHandle() {
            if(!this.permission_script){
                return;
            }
            this.gtm.is_edit = true;
        },

        ga4Handle() {
            if(!this.permission_script){
                return;
            }
            this.ga4.is_edit = true;
        },

        startFunction() {
            this.$ajax.get('/settings/custom-script-config/list').then(res => {

                res.data.data.forEach(item => {
                    item.is_edit = false;
                    item.is_show_error = false;
                    item.is_loading = false;

                    switch (item.script_name) {
                        case 'ga4':
                            this.ga4 = item;
                            break;
                        case 'google_tag_manager':
                            this.gtm = item;
                            break;
                    }
                });
                this.is_loading = false;
            }).catch(err => {
                console.log(err);
            });
        },

        close(type){
            this[type].is_edit = false;
            if(this[type].is_show_error) this[type].container_id='';
            this[type].is_show_error=false;
        },

        save(type) {
            let item = null;

            const fdata = {};

            switch (type) {
                case 'gtm':
                    item = this.gtm;
                    fdata.script_name = 'google_tag_manager';
                    break;

                case 'ga4':
                    item = this.ga4;
                    fdata.script_name = 'ga4';
                    break;
            }

            // const is_update = item.id;

            if (item.container_id === '') {
                item.is_show_error = true;
                this.err_message='Container ID cannot be empty.';
                return;
            } else {
                switch (type) {
                    case 'gtm':
                        item.is_show_error= /^GTM-[a-zA-Z0-9]+$/i.test(item.container_id)? false : true;
                        break;
                    case 'ga4':
                        item.is_show_error=/^G-[a-zA-Z0-9]+$/i.test(item.container_id)? false : true;
                        break;
                }
                this.err_message='Please input correct container ID.';

                if(item.is_show_error){
                    return;
                };
            };

            fdata.container_id = item.container_id;
            fdata.status = item.status;


          let text = 'Script Added';
            if (item.id) {
                text = 'Script Updated';
                fdata.id = item.id;
            } else {
                fdata.status = 100;
            }

            item.is_loading = true;

            this.$ajax.post('/settings/custom-script-config/save', fdata).then(res => {
                if (res.data.status === 'success') {
                    item.id = res.data.data.id;
                    item.status = res.data.data.status;
                    item.updated_at = res.data.data.updated_at;
                    this.$notify(`<svg><use xlink:href="#yes--sprite"></use></svg> ${text}`);
                }

              item.is_loading = false;

            }).catch(err => {
                item.is_loading = false;

                console.log(err);
            });
            // let item = this.script_data.find(e => {
            //     return e.id == val.id;
            // });

            // if (item) {
            //     item.container_id = val.container_id;
            //     item.status = val.status;
            //     item.script_name = val.script_name;
            //     item.updated_at = val.updated_at;
            // } else {
            //     this.script_data.push(val);
            // }


        },

        remove(type){
            let item = null;

            switch (type) {
                case 'gtm':
                    item = this.gtm;
                    break;

                case 'ga4':
                    item = this.ga4;
                    break;
            }

            item.is_loading = true;

            const fdata = {};
            fdata.id = item.id;

            this.$ajax({
                method: 'post',
                url: '/settings/custom-script-config/remove',
                data: fdata
            }).then(res => {
                item.is_loading = false;
                item.id = 0;
                item.container_id = '';
                item.status = 0;
                item.is_show_error = false;
                item.updated_at = '';
                this.$notify(`<svg><use xlink:href="#yes--sprite"></use></svg> Script removed`);

            }).catch(err => {
                item.is_loading = false;
            });


        }
    },

    mounted() {
        this.is_merchant_host = this.$store.state.user_data.merchant_account.is_enable_script_manager && !this.$store.state.merchant_disabeld;
        // this.is_merchant_host = true;
        if (this.is_merchant_host) {
            this.startFunction();
        } else {
            this.is_loading = false;
        }
    },
}
</script>
<style lang="scss">
.settings-script {
    max-width: 820px;

    .head {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .disabled {
            pointer-events: none;
        }
    }

    .desc {
        margin: 8px 0;
        font: var(--font-fg-14);
        color: var(--black-75);

        a {
            cursor: pointer;
        }
    }

    .ui-alert {
        margin-bottom: 0;
    }

    .script-box {
        margin: 16px 0;

        &.is-loading {
            opacity: .5;
            pointer-events: none;
        }

        .box {
            display: flex;
            align-items: center;
            border-radius: 8px;
            background-color: var(--white);
            border: 1px solid var(--black-20);
            cursor: pointer;

            img {
                height: 48px;
                display: block;
                margin-left: 8px;
            }

            .text-id {
                flex: 1;
                padding-left: 16px;

                h3 {
                    font: var(--font-fg-16);
                    color: var(--black-90);
                    margin-bottom: 2px;
                }

                p {
                    color: var(--black-75);
                    font-size: 12px;
                }
            }

            .right {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                padding: 16px;
                min-height: 62px;

                time {
                    font: var(--font-fg-12);
                    color: var(--black-75);
                    padding-right: 16px;
                }

                .link {
                    font: var(--font-fg-14);
                    color: var(--dark-blue);
                    padding-right: 6px;
                }

                .act {
                    font: var(--font-fg-12);
                    display: flex;
                    align-items: center;
                    color: var(--dark-blue);

                    &::before {
                        content: '';
                        display: block;
                        height: 10px;
                        width: 10px;
                        border-radius: 50%;
                        background-color: var(--green);
                        margin-right: 8px;
                    }
                }

                .act-disabled {
                    color: var(--black-50);

                    &::before {
                        background-color: var(--black-50);
                    }
                }

                .svg-box {
                    width: 32px;
                    height: 32px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: 8px;

                    svg {
                        fill: var(--dark-blue);
                        height: 12px;
                        width: 12px;
                        display: block;
                    }

                    &.not-allowed-btn{
                        cursor: not-allowed;
                        pointer-events: none;

                        svg {
                            fill: var(--black-50);
                        }
                    }
                }
            }
        }

        .edit-box {
            padding: 8px 16px 16px;
            background-color: var(--bg-02);
            border: 1px solid var(--bg-02);
            border-radius: 8px;

            header {
                display: flex;
                align-items: center;

                h2 {
                    font: var(--font-fg-16);
                    color: var(--black-90);
                    margin-right: 16px;
                    flex: 1;
                }

                time {
                    font: var(--font-fg-12);
                    color: var(--black-75);
                }

                i {
                    cursor: pointer;
                    height: 32px;
                    width: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    right: -8px;

                    svg {
                        width: 10px;
                        height: 10px;
                        fill: var(--black-90);
                    }

                    &:hover {
                        svg {
                            fill: var(--dark-blue);
                        }
                    }
                }
            }

            .text {
                margin: 16px 0;
                display: flex;
                align-items: flex-start;

                img {
                    display: block;
                    max-width: 260px;
                    width: 40%;
                    height: auto;
                }

                p {
                    padding-left: 16px;
                    font-size: 12px;

                    a {
                        color: var(--blue);
                        text-decoration: underline;

                        &:hover {
                            text-decoration: none;
                        }
                    }
                }
            }

            .form-item {
                margin: 16px 0;
                display: flex;
                align-items: center;

                label {
                    font: var(--font-fg-16);
                    color: var(--black-90);
                    width: 120px;
                }

                input {
                    flex: 1;
                    border-radius: 6px;
                    padding: 10px;
                    display: block;
                    color: var(--black-90);
                    border: 1px solid var(--black-40);
                    font-size: 14px;

                    &.error {
                        border-color: var(--alert);
                    }

                    &:focus {
                        outline: none;
                        border-color: var(--blue);
                    }

                    &::placeholder {
                        color: var(--black-50);
                    }
                }
            }

            .form-submit {
                display: flex;
                align-items: center;
                margin-top: 12px;

              & > div {
                    flex: 1;

                    & > span {
                        cursor: pointer;
                        display: inline-flex;
                        align-items: center;
                        color: var(--black-50);
                        font: var(--font-fg-14);
                    }
                }

                .ui-switch {
                    margin-right: 16px;
                    margin-left: 8px;
                }

                .remove {
                    cursor: pointer;
                    font: var(--font-fg-14);
                    color: var(--alert);
                    margin-right: 16px;
                }
            }

            .error-msg {
                margin:  0;
                font-size: 12px;
                color: var(--alert);
                text-align: right;
                margin-top: -12px;

                &::before {
                    content: '*';
                }
            }
        }

    }

    .not-allowed-box{
        .box{
            cursor: default;
        }
        
    }

}
</style>

